import React, { Component } from "react";
import { connect } from "react-redux";
import mapboxgl from "mapbox-gl";
import axios, { CancelToken } from "axios";
import { circle, point } from "@turf/turf";
import _ from "lodash";

import { I18n } from "react-redux-i18n";

import { setAlert } from "../../../actions/alertActions";
import { MAPBOX_TOKEN, calculateDistance, parseAddress } from "../Utils";

import "mapbox-gl/dist/mapbox-gl.css";
import "mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import "../../../styles/components/utils/components/Minimap.scss";

const handleMapsResults = (results) => {
  if (results.length > 0) {
    const address_components = results[0].address_components;
    if (address_components) {
      const route = address_components.find((c) => c.types.includes("route"));
      const street = route ? route.short_name : "";

      const street_number = address_components.find((c) =>
        c.types.includes("street_number")
      );
      const number = street_number ? street_number.long_name : "";

      const postal_code = address_components.find((c) =>
        c.types.includes("postal_code")
      );
      const zipCode = postal_code ? postal_code.long_name : "";

      const locality = address_components.find((c) =>
        c.types.includes("locality")
      );
      const city = locality ? locality.short_name : "";

      const aal1 = address_components.find((c) =>
        c.types.includes("administrative_area_level_1")
      );
      const state = aal1 ? aal1.short_name : "";

      const country_obj = address_components.find((c) =>
        c.types.includes("country")
      );
      const country = country_obj ? country_obj.long_name : "";

      const address = { street, number, zipCode, city, state, country };
      return address;
    }
  }
};

class Minimap extends Component {
  constructor(props) {
    super(props);
    this._mounted = false;
    this.cancel = null;
    this.UPDATE_RADIUS = this.props.mapUpdateRadius ?? 500;

    mapboxgl.accessToken = this.props.brand.tokenMapBox || MAPBOX_TOKEN;

    this.state = {
      lat: 41.172707,
      lng: -8.680059,
      zoom: 14,
      map: null,

      currCoords: [-8.680059, 41.172707],
      flyTo: false,
      wasDragged: false,
    };

    this.updateCoords = this.updateCoords.bind(this);
    this.updateZoom = this.updateZoom.bind(this);
    this.invalidAddressOrCoords = this.invalidAddressOrCoords.bind(this);
    this.geocode = this.geocode.bind(this);
    this.updateAddress = this.updateAddress.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
    let el = document.createElement("div");
    el.className = "marker";
    el.style.backgroundImage = `url('${
      this.props.brand?.icons?.mapIcons?.[
        `marker_${_.camelCase(this.props.brand?.brandName)}`
      ]
    }')`;

    this.safeSetState(
      {
        map: new mapboxgl.Map({
          container: this.mapContainer,
          style: "mapbox://styles/evioadmin/ck9n0oy82423g1inp0fngkjlr",
          center: Array.isArray(this.props.coords)
            ? this.props.coords
            : [this.state.lng, this.state.lat],
          zoom: this.state.zoom,
          minZoom: 14,
          attributionControl: false,
          logoPosition: "bottom-right",
        }),
        marker: new mapboxgl.Marker(el),
      },
      () => {
        this.state.map.on("load", () => {
          this.state.map.resize();
          this.state.marker
            .setLngLat([this.state.lng, this.state.lat])
            .addTo(this.state.map);
          const circleData = circle(
            point([this.state.lng, this.state.lat]),
            this.UPDATE_RADIUS,
            {
              units: "meters",
            }
          );
          this.state.map.addSource("radiusData", {
            type: "geojson",
            data: circleData,
          });
          this.state.map.addLayer({
            id: "circleRadius",
            source: "radiusData",
            type: "fill",
            paint: {
              "fill-color":
                this.props.brand?.colors?.secondaryColor || "#2fffd1",
              "fill-opacity": 0.15,
            },
          });
        });

        this.state.map.on("move", () => {
          const { lat, lng } = this.state.map.getCenter();
          this.updateCoords(lat, lng);

          const zoom = this.state.map.getZoom().toFixed(2);
          this.updateZoom(zoom);
        });

        this.state.map.on("dragend", () => {
          const { lat, lng } = this.state;
          this.safeSetState({ wasDragged: true }, () => {
            this.updateAddress(lat, lng, this.state.wasDragged);
          });
        });

        this.state.map.on("moveend", () => this.safeSetState({ flyTo: false }));

        if (this.props.query !== "")
          this.geocode(
            this.props.query,
            this.props.charger?.geometry?.coordinates,
            this.props.charger?.geometry?.coordinates
          );
        /* else if (Array.isArray(this.props.charger?.geometry?.coordinates)) {
          const [lng, lat] = this.props.charger?.geometry?.coordinates;
          this.updateAddress(lat, lng);
        } */
      }
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.query !== this.props.query) {
      this.geocode(this.props.query);
    } else if (
      Array.isArray(this.props.coords) &&
      (prevProps?.coords?.[0] !== this.props.coords?.[0] ||
        prevProps?.coords?.[1] !== this.props.coords?.[1])
    ) {
      this.updateAddress(
        this.props.coords[1],
        this.props.coords[0],
        this.props.forceCoords || this.state.wasDragged
      );
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this.cancel) this.cancel("Component will unmount");
  }

  updateCoords(lat, lng) {
    this.safeSetState(
      {
        lat: lat,
        lng: lng,
      },
      () => this.state.marker.setLngLat([lng, lat])
    );
  }

  updateZoom(zoom) {
    this.safeSetState({ zoom: zoom });
  }

  invalidAddressOrCoords(type) {
    this.props.setAlert(I18n.t(`minimap_${type}Invalid`), "danger");
  }

  updateAddress(lat, lng, forceCoords = false, preventAddressUpdate = false) {
    const shouldUpdateAddress =
      !preventAddressUpdate &&
      calculateDistance(this.state.currCoords, [lng, lat], "meters") >=
        this.UPDATE_RADIUS;
    if (!this.state.flyTo)
      axios
        .get("/api/private/proxy", {
          params: {
            type: "MAPS",
            url: `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&location_type=ROOFTOP`,
          },
          cancelToken: new CancelToken((c) => (this.cancel = c)),
        })
        .then((res) => {
          const results = res.data.results;
          const address = handleMapsResults(results);

          if (address) {
            this.safeSetState(
              {
                flyTo: !this.state.wasDragged,
                ...(shouldUpdateAddress && {
                  currCoords: forceCoords
                    ? [lng, lat]
                    : [this.state.lng, this.state.lat],
                }),
              },
              () => {
                if (!this.state.wasDragged) {
                  this.state.map.flyTo({
                    center: [lng, lat],
                  });
                }
              }
            );
            this.props.updateAddress(
              {
                ...(shouldUpdateAddress && { address }),
                coords: forceCoords
                  ? [lng, lat]
                  : [this.state.lng, this.state.lat],
              },
              () => {
                if (shouldUpdateAddress) {
                  const circleData = circle(
                    point(
                      forceCoords
                        ? [lng, lat]
                        : [this.state.lng, this.state.lat]
                    ),
                    this.UPDATE_RADIUS,
                    { units: "meters" }
                  );
                  this.state.map.getSource("radiusData")?.setData(circleData);
                }
                this.safeSetState({ wasDragged: false });
              }
            );
          } else this.invalidAddressOrCoords("coords");
        })
        .catch((err) =>
          this.props.setAlert(I18n.t(err.response.data.message), "danger")
        );
  }

  geocode(
    address,
    forceCoords = false,
    forceRadiusCenterToGeocodedAddress = false
  ) {
    axios
      .get("/api/private/proxy", {
        params: {
          type: "MAPS",
          url: `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            address
          )}&location_type=ROOFTOP`,
        },
        cancelToken: new CancelToken((c) => (this.cancel = c)),
      })
      .then((res) => {
        const results = res.data.results;
        const address = handleMapsResults(results);

        if (address) {
          this.safeSetState(
            {
              flyTo: true,
              currCoords:
                forceCoords && !forceRadiusCenterToGeocodedAddress
                  ? this.props.coords
                  : [
                      results[0].geometry.location.lng,
                      results[0].geometry.location.lat,
                    ],
            },
            () =>
              this.state.map.flyTo({
                center: forceCoords
                  ? this.props.coords
                  : [
                      results[0].geometry.location.lng,
                      results[0].geometry.location.lat,
                    ],
                zoom: 15,
              })
          );
          this.props.updateAddress(
            {
              address,
              coords: forceCoords
                ? this.props.coords
                : [
                    results[0].geometry.location.lng,
                    results[0].geometry.location.lat,
                  ],
            },
            () => {
              const circleData = circle(
                point(
                  forceCoords && !forceRadiusCenterToGeocodedAddress
                    ? this.props.coords
                    : [
                        results[0].geometry.location.lng,
                        results[0].geometry.location.lat,
                      ]
                ),
                this.UPDATE_RADIUS,
                { units: "meters" }
              );
              this.state.map.getSource("radiusData")?.setData(circleData);
            }
          );
        } else this.invalidAddressOrCoords("address");
      })
      .catch((err) =>
        this.props.setAlert(I18n.t(err.response.data.message), "danger")
      );
  }

  safeSetState(params, callback) {
    if (this._mounted) this.setState(params, callback);
  }

  render() {
    return (
      <div id="minimap">
        <div className="minimap-overview">
          <div
            ref={(el) => (this.mapContainer = el)}
            className="mapContainer mapboxgl-map"
          />
          <p className="address">{parseAddress(this.props.address)}</p>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  mapUpdateRadius: state.account.accountData.userPackage?.mapUpdateRadius,
  brand: state.app.brand,
});

export default connect(mapStateToProps, { setAlert })(Minimap);
